import React, { useEffect, useState, useCallback } from 'react';
import { dispatchAPI, contractsAPI, inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Truck, Plus, X, Loader2, Download, Mail, CheckCircle2, Package, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Simple, self-contained dispatch flow: pick a customer, pick parts that have
// stock, create a delivery challan, then optionally email the PDF to the customer.
export default function QuickDispatch() {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);

  const [customerId, setCustomerId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [rows, setRows] = useState([{ item_id: '', godown_id: '', quantity: '' }]);
  const [creating, setCreating] = useState(false);
  const [showWeight, setShowWeight] = useState(false);
  const [emailAfter, setEmailAfter] = useState(false);
  // Fixed assets are not dispatch stock — opt in explicitly when needed.
  const [includeFixedAssets, setIncludeFixedAssets] = useState(false);

  // Result of the last created challan (drives the email/download panel)
  const [created, setCreated] = useState(null);

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', cc_email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [custRes, itemRes, godownRes, stockRes] = await Promise.all([
        contractsAPI.activeCustomers(),
        inventoryAPI.listItems({ limit: 1000 }),
        inventoryAPI.listGodowns(),
        inventoryAPI.getStock(),
      ]);
      setCustomers(custRes.data || []);
      setItems(itemRes.data?.items || itemRes.data || []);
      setGodowns(godownRes.data || []);
      setStock(stockRes.data || []);
    } catch { toast.error('Failed to load dispatch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Godowns items may NOT be dispatched from in the simple flow: Store (returns
  // only) plus non-OK / scrap holding areas — operators dispatch good FG stock.
  const BLOCKED_GODOWN_CODES = ['STORE', 'QUARANTINE', 'REJECTION', 'SCRAP', 'REGRIND', 'INBOUND_STAGING'];
  const isDispatchableGodown = (g) => g && !BLOCKED_GODOWN_CODES.includes(g.code);

  // Map: item_id -> [{ godown_id, quantity }] for positive, dispatchable stock.
  const stockByItem = React.useMemo(() => {
    const godownMap = new Map(godowns.map(g => [g.id, g]));
    const m = new Map();
    (stock || []).forEach(s => {
      if (!(s.quantity > 0)) return;
      const g = godownMap.get(s.godown_id);
      if (!isDispatchableGodown(g)) return;
      if (!m.has(s.item_id)) m.set(s.item_id, []);
      m.get(s.item_id).push({ godown_id: s.godown_id, quantity: s.quantity });
    });
    return m;
  }, [stock, godowns]);

  const itemById = React.useMemo(() => new Map(items.map(i => [i.id, i])), [items]);  const godownById = React.useMemo(() => new Map(godowns.map(g => [g.id, g])), [godowns]);

  // Only parts that currently have dispatchable stock are selectable.
  // Fixed assets are excluded unless explicitly included.
  const partOptions = React.useMemo(() => {
    return items
      .filter(i => stockByItem.has(i.id))
      .filter(i => includeFixedAssets || i.item_type !== 'Fixed Asset')
      .map(i => {
        const total = (stockByItem.get(i.id) || []).reduce((s, r) => s + r.quantity, 0);
        return {
          value: i.id,
          label: `${i.unique_id} - ${i.item_name}`,
          sub: `Available: ${total} ${i.unit_of_measure || ''}`,
          searchStr: `${i.unique_id} ${i.item_name}`,
        };
      });
  }, [items, stockByItem, includeFixedAssets]);

  const customerName = (customers.find(c => c.id === customerId) || {}).customer_name || '';
  const customerEmail = (customers.find(c => c.id === customerId) || {}).email || '';

  const availFor = (itemId, godownId) => {
    const recs = stockByItem.get(itemId) || [];
    const r = recs.find(x => x.godown_id === godownId);
    return r ? r.quantity : 0;
  };

  // Optional shipping weight: per-unit kg + line weight for a dispatch row.
  const perUnitKg = (item) => {
    if (!item) return null;
    const v = item.unit_weight_value, per = item.unit_weight_per_qty || 1;
    if (v == null || v === '' || !per) return null;
    const kg = item.unit_weight_uom === 'gram' ? Number(v) / 1000 : Number(v);
    return kg / Number(per);
  };
  const lineWeightKg = (itemId, qty) => {
    const pu = perUnitKg(itemById.get(itemId));
    if (pu == null) return null;
    return pu * (parseFloat(qty) || 0);
  };
  const totalWeightKg = rows.reduce((s, r) => {
    const w = r.item_id ? lineWeightKg(r.item_id, r.quantity) : null;
    return s + (w || 0);
  }, 0);
  const anyMissingWeight = rows.some(r => r.item_id && perUnitKg(itemById.get(r.item_id)) == null);

  const setRow = (idx, patch) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const onPickPart = (idx, itemId) => {
    const recs = stockByItem.get(itemId) || [];
    // Auto-pick the godown when only one has stock.
    const godown_id = recs.length === 1 ? recs[0].godown_id : '';
    setRow(idx, { item_id: itemId, godown_id, quantity: '' });
  };

  const addRow = () => setRows(prev => [...prev, { item_id: '', godown_id: '', quantity: '' }]);
  const removeRow = (idx) => setRows(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));

  const resetForm = () => {
    setCustomerId('');
    setRemarks('');
    setRows([{ item_id: '', godown_id: '', quantity: '' }]);
    setCreated(null);
  };
  const handleCreate = async () => {
    if (!customerId) { toast.error('Select a customer'); return; }
    const valid = rows.filter(r => r.item_id && r.godown_id && parseFloat(r.quantity) > 0);
    if (valid.length === 0) { toast.error('Add at least one part with a godown and quantity'); return; }
    for (const r of valid) {
      const avail = availFor(r.item_id, r.godown_id);
      if (parseFloat(r.quantity) > avail) {
        const nm = itemById.get(r.item_id)?.item_name || r.item_id;
        toast.error(`Insufficient stock for ${nm}. Available: ${avail}`);
        return;
      }
    }
    setCreating(true);
    try {
      const res = await dispatchAPI.createChallan({
        customer_id: customerId,
        dispatch_type: 'delivery_challan',
        remarks,
        show_weight_on_challan: showWeight,
        items: valid.map(r => ({ item_id: r.item_id, quantity: parseFloat(r.quantity), godown_id: r.godown_id })),
      });
      setCreated(res.data);
      toast.success(`Dispatch challan ${res.data.dc_number} created`);
      if (emailAfter) await autoEmail(res.data);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create dispatch challan');
    } finally { setCreating(false); }
  };

  const autoEmail = async (challan) => {
    let emails = [];
    try {
      const res = await dispatchAPI.getCustomerDispatchEmails(customerId);
      emails = res.data?.emails || [];
    } catch { /* fall back below */ }
    if (!emails.length) {
      (customerEmail || '').split(',').forEach(e => { const t = e.trim(); if (t) emails.push(t); });
    }
    if (!emails.length) {
      toast.error('No customer contact email on file — use the Email button to enter one', { duration: 10000 });
      return;
    }
    try {
      await dispatchAPI.emailToCustomer({
        customer_id: challan.customer_id, challan_ids: [challan.id],
        to_email: emails.join(', '), cc_email: '',
        subject: `Dispatch Challan ${challan.dc_number} - ${challan.customer_name}`,
        message: '', include_challan_pdf: true, include_coc_bundle: false,
      });
      toast.success(`Challan emailed to ${emails.join(', ')}`, { duration: 8000 });
    } catch (err) {
      toast.error(`Challan created but the email failed: ${err.response?.data?.detail || 'unknown error'}`,
        { duration: 12000 });
    }
  };

  const downloadPDF = async () => {
    if (!created) return;
    try {
      toast.info('Generating challan PDF...');
      const res = await dispatchAPI.downloadPDF(created.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Challan_${created.dc_number}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch { toast.error('Failed to download PDF'); }
  };

  const openEmail = async () => {
    if (!created) return;
    let emails = [];
    try {
      const res = await dispatchAPI.getCustomerDispatchEmails(customerId);
      emails = res.data?.emails || [];
    } catch { /* fall back to the customer master email */ }
    if (!emails.length) {
      (customerEmail || '').split(',').forEach(e => { const t = e.trim(); if (t) emails.push(t); });
    }
    setEmailForm({
      to_email: emails.join(', '),
      cc_email: '',
      subject: `Dispatch Challan ${created.dc_number} - ${created.customer_name}`,
      message: '',
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!created) return;
    if (!emailForm.to_email.trim()) { toast.error('Enter recipient email'); return; }
    setSending(true);
    try {
      await dispatchAPI.emailToCustomer({
        customer_id: created.customer_id,
        challan_ids: [created.id],
        to_email: emailForm.to_email,
        cc_email: emailForm.cc_email,
        subject: emailForm.subject,
        message: emailForm.message,
        include_challan_pdf: true,
        include_coc_bundle: false,
      });
      toast.success('Challan emailed to customer');
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="py-16 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" /></div>;
  }

  // Success panel after a challan is created.
  if (created) {
    return (
      <div className="max-w-2xl mx-auto py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Dispatch Challan Created
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" data-testid="quick-dispatch-success">
            <div className="rounded border bg-slate-50 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-base">{created.dc_number}</span>
                <Badge variant="outline" className="text-[10px]">{created.total_items} item(s)</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{created.customer_name}</div>
              <div className="mt-2 space-y-1">
                {(created.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span>{it.item_unique_id} — {it.item_name}</span>
                    <span className="font-mono">{it.quantity} {it.unit_of_measure} · {it.godown_name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={downloadPDF} data-testid="quick-download-pdf-btn">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={openEmail} data-testid="quick-email-btn">
                <Mail className="w-4 h-4 mr-1" /> Email to Customer
              </Button>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={resetForm} data-testid="quick-new-dispatch-btn">
                <Plus className="w-4 h-4 mr-1" /> New Dispatch
              </Button>
            </div>
          </CardContent>
        </Card>

        <EmailDialog open={emailOpen} onOpenChange={setEmailOpen} form={emailForm} setForm={setEmailForm}
          onSend={sendEmail} sending={sending} dcNumber={created.dc_number} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-2" data-testid="quick-dispatch-page">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="w-5 h-5 text-blue-600" /> Quick Dispatch
          </CardTitle>
          <p className="text-xs text-muted-foreground">Pick a customer and the parts in stock — a delivery challan is created and can be emailed.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Customer</Label>
            <SearchableSelect
              options={customers.map(c => ({ value: c.id, label: c.customer_name, sub: c.customer_code || '', searchStr: `${c.customer_name} ${c.customer_code || ''}` }))}
              value={customerId} onSelect={setCustomerId} placeholder="Select customer..." testId="quick-customer-select" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Parts to dispatch</Label>
              <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={includeFixedAssets}
                  onChange={e => setIncludeFixedAssets(e.target.checked)}
                  className="h-3.5 w-3.5 accent-slate-600" data-testid="quick-include-fixed-assets" />
                Include fixed assets
              </label>
            </div>
            {rows.map((r, idx) => {
              const recs = stockByItem.get(r.item_id) || [];
              const avail = r.godown_id ? availFor(r.item_id, r.godown_id) : recs.reduce((s, x) => s + x.quantity, 0);
              return (
                <div key={idx} className="border rounded p-2 space-y-2" data-testid={`quick-row-${idx}`}>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px]">Part (in stock)</Label>
                      <SearchableSelect options={partOptions} value={r.item_id}
                        onSelect={(v) => onPickPart(idx, v)} placeholder="Select part..." testId={`quick-part-${idx}`} />
                    </div>
                    <Button variant="ghost" size="sm" className="h-9 text-rose-600" onClick={() => removeRow(idx)} data-testid={`quick-remove-${idx}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {r.item_id && (
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-[10px]">Godown</Label>
                        <Select value={r.godown_id} onValueChange={(v) => setRow(idx, { godown_id: v })}>
                          <SelectTrigger className="h-9 text-sm" data-testid={`quick-godown-${idx}`}>
                            <SelectValue placeholder="Select godown..." />
                          </SelectTrigger>
                          <SelectContent>
                            {recs.map(rec => (
                              <SelectItem key={rec.godown_id} value={rec.godown_id}>
                                {godownById.get(rec.godown_id)?.name || rec.godown_id} — {rec.quantity} avl
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <Label className="text-[10px]">Quantity</Label>
                        <Input type="number" step="any" min="0" value={r.quantity}
                          onChange={e => setRow(idx, { quantity: e.target.value })}
                          className="h-9 text-sm" data-testid={`quick-qty-${idx}`} />
                      </div>
                      <div className="text-[10px] text-muted-foreground pb-2 whitespace-nowrap">avl {avail}</div>
                    </div>
                  )}
                  {r.item_id && (
                    perUnitKg(itemById.get(r.item_id)) == null ? (
                      <div className="text-[11px] text-orange-600 flex items-center gap-1" data-testid={`quick-weight-warning-${idx}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Weight not configured for this item — add it in Item Master to include shipment weight.
                      </div>
                    ) : (
                      parseFloat(r.quantity) > 0 && (
                        <div className="text-[11px] text-muted-foreground" data-testid={`quick-weight-${idx}`}>
                          Weight: <span className="font-medium text-foreground">{(lineWeightKg(r.item_id, r.quantity) || 0).toFixed(3)} kg</span>
                        </div>
                      )
                    )
                  )}
                </div>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRow} data-testid="quick-add-row">
              <Plus className="w-3 h-3 mr-1" /> Add part
            </Button>
          </div>

          <div>
            <Label className="text-xs">Remarks (optional)</Label>
            <Input value={remarks} onChange={e => setRemarks(e.target.value)} className="h-9 text-sm" data-testid="quick-remarks" />
          </div>

          <div className="rounded border p-2.5 bg-muted/20 space-y-1.5">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="quick-email-challan-toggle">
              <input type="checkbox" checked={emailAfter} onChange={e => setEmailAfter(e.target.checked)}
                className="h-3.5 w-3.5 accent-teal-600" />
              Email challan to the customer contacts right after creating
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="quick-show-weight-toggle">
              <input type="checkbox" checked={showWeight} onChange={e => setShowWeight(e.target.checked)} className="h-3.5 w-3.5 accent-blue-600" />
              Show per-line weights on the challan PDF (the total is always printed)
            </label>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Total dispatch weight{showWeight ? ' (per-line weights also printed)' : ' (printed on the challan)'}:</span>
              <span className="font-semibold" data-testid="quick-total-weight">{totalWeightKg.toFixed(3)} kg</span>
            </div>
            {anyMissingWeight && (
              <p className="text-[11px] text-orange-600 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Some items have no weight configured; the total excludes them.
              </p>
            )}
          </div>

          {partOptions.length === 0 && (
            <div className="text-xs text-amber-600 flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> No parts currently have dispatchable stock.
            </div>
          )}

          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={creating} data-testid="quick-create-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}
              Create Dispatch Challan
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EmailDialog({ open, onOpenChange, form, setForm, onSend, sending, dcNumber }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quick-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Email Challan {dcNumber}</DialogTitle>
          <DialogDescription>The challan PDF will be attached. Edit the recipients and message before sending.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">To</Label>
            <Input value={form.to_email} onChange={e => setForm(f => ({ ...f, to_email: e.target.value }))}
              placeholder="customer@example.com" className="h-9 text-sm" data-testid="quick-email-to" />
          </div>
          <div>
            <Label className="text-xs">CC (optional)</Label>
            <Input value={form.cc_email} onChange={e => setForm(f => ({ ...f, cc_email: e.target.value }))}
              className="h-9 text-sm" data-testid="quick-email-cc" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              className="h-9 text-sm" data-testid="quick-email-subject" />
          </div>
          <div>
            <Label className="text-xs">Message (optional)</Label>
            <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              rows={3} className="text-sm" data-testid="quick-email-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={onSend} disabled={sending} data-testid="quick-email-send">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
